import { cn } from "@/shared/lib/utils";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type ButtonHTMLAttributes } from "react";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-normal shadow-xs transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Existing default, kept for compatibility but might be less used.
        default:
          "bg-primary text-primary-foreground hover:bg-primary/90 focus-visible:ring-1 focus-visible:ring-ring",
        // New primary button style
        blue:
          "bg-blue-600 text-white hover:bg-blue-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 focus-visible:ring-1 focus-visible:ring-ring",
        "destructive-outline":
          "border border-destructive text-destructive bg-transparent hover:bg-destructive/10 focus-visible:ring-1 focus-visible:ring-destructive",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground focus-visible:ring-1 focus-visible:ring-ring",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 focus-visible:ring-1 focus-visible:ring-ring",
        ghost:
          "hover:bg-accent hover:text-accent-foreground focus-visible:ring-1 focus-visible:ring-ring",
        link: "text-primary underline-offset-4 hover:underline focus-visible:ring-1 focus-visible:ring-ring",
        plain:
          "text-gray-900 hover:text-gray-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-600", // For Cancel button
      },
      size: {
        // Redefined sizes based on new design
        default: "rounded-md px-3 py-2 text-sm", // New default size
        xs: "rounded-sm px-2 py-1 text-xs", // New smallest size
        sm: "rounded-sm px-2 py-1 text-sm", // New small size with text-sm
        md: "rounded-md px-2.5 py-1.5 text-sm", // New medium size
        lg: "rounded-md px-3.5 py-2.5 text-sm", // New large size
        icon: "h-9 w-9 rounded-md", // Kept icon, ensured rounded-md
      },
    },
    defaultVariants: {
      variant: "blue", // Changed default variant to blue
      size: "default",
    },
  }
);

export interface ButtonProperties
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProperties>(
  ({ className, variant, size, asChild = false, ...properties }, reference) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={reference}
        {...properties}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants }; // eslint-disable-line react-refresh/only-export-components
export type { ButtonProperties as ButtonProps }; // Export alias for backward compatibility
